import {
  Component,
  Suspense,
  lazy,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import type { LottieProps } from "react-lottie-player";

import { captureException } from "@/observability/sentry";

const Lottie = lazy(() => import("react-lottie-player"));

/**
 * Lottie rendering has crashed in production before, and on these pages it only
 * ever draws decoration, a failure must cost the illustration, not the page:
 * https://webshare-proxy.sentry.io/issues/7010088700/?project=4503931616100352
 */
class LottieBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const wrapped = new Error(
      "Problem with Lottie. This is handled gracefully."
    );
    wrapped.cause = error;
    captureException(wrapped, { extra: { errorInfo: JSON.stringify(info) } });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * The blog's Lottie call sites, which are all decorative.
 *
 * The mount gate is not a hydration nicety: `react-lottie-player` reaches for
 * `document` while its module body evaluates, so importing it inside the SSR
 * pass throws before any boundary here can see it, and these routes are
 * prerendered.
 */
export default function MarketingLottie(props: LottieProps) {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  if (!hydrated) {
    return null;
  }

  return (
    <LottieBoundary>
      <Suspense fallback={null}>
        <Lottie {...props} />
      </Suspense>
    </LottieBoundary>
  );
}
